import { format, subMonths } from 'date-fns';

import { action } from 'lib/actionHelpers';
import { formatLeadsByMedium } from 'lib/reportingUtils';
import { store } from 'redux/lib/configureStore.js';

// const RANKING_INSIGHT_KEYS = ["top5", "top10", "top30"];
const RANKING_INSIGHT_KEYS = ['today_avg', 'top5', 'top10'];

const today = new Date();

// const current_year = format(today, "yyyy");
const current_month_full_date = format(today, 'MM/dd/yyyy');

const getMonthsToCalculate = (amt, data, indexOfMonth) => {
  const months_included = [];
  const month_value_map = data.reduce((all, item) => {
    all[item.label] = Number(item.currentData);
    months_included.push(item.label);
    return all;
  }, {});

  const mostRecentMonthValues = [];
  const monthsToCompareTo = [];

  for (let i = 0; i < amt * 2; i++) {
    // const current_month = format(today, "MMM");
    const month_formatted = format(subMonths(new Date(current_month_full_date), i + 1), 'MMM');
    if (i < amt) {
      mostRecentMonthValues.push(month_value_map[month_formatted]);
    } else {
      monthsToCompareTo.push(month_value_map[month_formatted]);
    }
  }
  // (data[indexVisible] || []) format should be formatted to [{value, label, description, color??? maybe}]

  const sumCurrent = mostRecentMonthValues.reduce((all, item) => {
    all += item;
    return all;
  }, 0);

  const sumPrev = monthsToCompareTo.reduce((all, item) => {
    all += item;
    return all;
  }, 0);

  const percentageIncrease = sumCurrent - sumPrev;
  const finalIncrease = (percentageIncrease / sumPrev) * 100;
  const percentageDecrease = sumPrev - sumCurrent;
  const finalDecrease = (percentageDecrease / sumPrev) * 100;

  const best_months_data = data.filter((item) => {
    if (months_included.includes(item.label)) {
      return item;
    }
  });

  const best_month_value = Math.max(...best_months_data.map((e) => e.currentData));
  const best_month_data = best_months_data.find((month) => Number(month.currentData) === Number(best_month_value));

  const is_an_increase = sumCurrent > sumPrev;
  const amountToLabel = amt * 30;

  const final = {
    label: `New visitors last ${amountToLabel}`,
    value: is_an_increase ? sumCurrent : null,
    current_sum: sumCurrent,
    previous_sum: sumPrev,
    percentage: null,
    isAnIncrease: null,
    percentage_description: `Over the past ${amountToLabel} days`,
    description: `You had the highest number of visitors in ${best_month_data.label}`,
  };

  if (sumCurrent !== 0 && sumPrev !== 0) {
    if (Math.sign(finalIncrease)) {
      final.percentage = `${finalIncrease.toFixed(1)}%`;
      final.isAnIncrease = true;
    } else {
      final.percentage = `${finalDecrease.toFixed(1)}%`;
      final.isAnIncrease = false;
    }
  }

  return final;
};

const calculateTopPercent = (data, percent) => {
  return calculatePercentage(data[`top_${percent}`], data.keyword_count);
};

const calculatePercentage = (num, denom) => {
  let percentage = (num / denom) * 100;
  return parseInt((percentage || 0).toFixed(0));
};

const TEXT_DESCRIPTION_RANKING_INSIGHTS = {
  today_avg: {
    label: 'Todays average',
    description: "Today's average position of your tracked keywords",
  },
  top10: {
    label: 'Top 10',
    description: 'Number of keywords that have a position better than 10',
  },
  top5: {
    label: 'Top 5',
    description: 'Number of keywords that have a position better than 5',
  },
};

const formatRankingInsights = (state) => {
  const arr = [];

  Object.entries(state).forEach(([key, value]) => {
    if (RANKING_INSIGHT_KEYS.includes(key)) {
      arr.push({
        value,
        label: TEXT_DESCRIPTION_RANKING_INSIGHTS[key].label,
        description: TEXT_DESCRIPTION_RANKING_INSIGHTS[key].description,
      });
    }
  });
  return arr;
};

const formatNewVisitors = (state) => {
  const final = (state || []).map((item) => {
    item['Previous year'] = (item.previousData || '').toString();
    item['Current year'] = (item.currentData || '').toString();
    item.currentData = (item.currentData || '').toString();
    item.previousData = (item.previousData || '').toString();
    return item;
  });
  action('FETCHING_ANALYTIC_INSIGHTS_NEW_VISITORS_SUCCESS', [...final]);
  return final;
};

const getAnalyticsRealTimeCard = (data) => {
  if (data.currentData > Number(data.previousData)) {
    let percentage = calculatePercentage(data.currentData, data.previousData);
    let description = `Your site is getting more traffic! Last month it received ${data.currentData - data.previousData} more organic new visitors compared to last year!`;
    if (percentage / 100 >= 1.5) {
      description = `Your site is breaking records, last month it received around ${Math.round(percentage / 100)}X the organic traffic compared to last year! 🎉 🎉 🎉`;
    }
    return {
      live: false,
      value: null,
      label: 'Analytics Insights',
      percentage: `${percentage}%`,
      isAnIncrease: true,
      percentage_description: `compared to last year`,
      description: description,
    };
  }
  return null;
};

const formatNewVisitorsAnalytics = (payload) => {
  const currentMonth = format(new Date(), 'MMM');
  // Get index of current month
  const indexOfMonth = (payload || [])
    .map((n) => {
      return n.label;
    })
    .indexOf(currentMonth);

  const indexLastMonth = indexOfMonth - 1;

  /*const monthsToCalculateNinety = getMonthsToCalculate(
    3,
    payload,
    indexOfMonth
  );*/

  const monthsToCalculateSixty = getMonthsToCalculate(2, payload, indexLastMonth);
  const monthsToCalculateThirty = getMonthsToCalculate(1, payload, indexLastMonth);

  let cards = [
    // comment out 90 days for now
    // monthsToCalculateNinety,
    monthsToCalculateSixty,
    monthsToCalculateThirty,
  ];

  let realTimeCard = getAnalyticsRealTimeCard((payload || {})[indexLastMonth] || {});

  if (realTimeCard !== null) {
    cards.unshift(realTimeCard);
  }

  return cards;
};

const formatAverageRankings = (payload) => {
  const data = (payload[0] || { daily_stats: {} }).daily_stats;
  const groupName = (payload[0] || {}).group_name || '';
  const average_positions = [];

  Object.entries(data).forEach(([key, value]) => {
    average_positions.push({
      date: format(new Date(key), 'MM/dd/yyyy'),
      ['Average Position']: value.average_position,
      ...value,
      ['Percent in top 10']: calculateTopPercent(value, 10),
      ['Percent in top 5']: calculateTopPercent(value, 5),
      ['Percent in top 1']: calculateTopPercent(value, 1),
    });
  });

  let minimum_value = null;
  let max_value_average_position = null;
  // Determine if a change in keyword counts has occured.

  average_positions.forEach((item, i) => {
    item.keyword_count_change = {
      hasChanged: false,
      description: '',
      value: 0,
    };
    // Determine data minimum value
    if (i === 0) {
      minimum_value = item['Percent in top 10'];
      item.minimum_value = minimum_value;

      max_value_average_position = parseInt(item['Average Position']).toFixed(0);
      item.max_value_average_position = max_value_average_position;
    }

    if (item['Percent in top 10'] < minimum_value) {
      minimum_value = item['Percent in top 10'];
      item.minimum_value = minimum_value;
    }

    if (item['Average Position'] > max_value_average_position) {
      max_value_average_position = parseInt(item['Average Position']).toFixed(0);
      item.max_value_average_position = max_value_average_position;
    }

    if (item['Percent in top 10'])
      if (i !== 0) {
        const prev = average_positions[i - 1];
        if (item.keyword_count !== prev.keyword_count) {
          let change = '';
          let value = 0;
          // Determine if keywords have been added or removed from
          if (item.keyword_count > prev.keyword_count) {
            change = 'added';
            value = item.keyword_count - prev.keyword_count;
          } else {
            change = 'removed';
            value = prev.keyword_count - item.keyword_count;
          }
          item.keyword_count_change = {
            hasChanged: true,
            description: `${value} keywords have been ${change}`,
            value,
          };
        }
      }
  });

  average_positions.forEach((item) => {
    item.minimum_value = minimum_value;
    item.max_value_average_position = parseInt(max_value_average_position);
  });

  formatRankingInsightsCards(average_positions[average_positions.length - 1], groupName);

  action('FORMAT_RANKINGS_GROUP_SUCCESS', {
    data: groupName,
  });

  action('FORMAT_AVERAGE_POSITION_SUCCESS', {
    data: average_positions,
  });
  action('FORMAT_PERCENTAGE_OF_KEYWORDS_IN_TOP_TEN_SUCCESS', {
    data: average_positions,
  });
};

const formatRankingInsightsCards = (payload, groupName) => {
  const keysMap = {
    keyword_count: 'Total Keywords',
    'Average Position': 'Average Position',
    'Percent in top 1': 'In top 1',
    'Percent in top 5': 'In top 5',
    'Percent in top 10': 'In top 10',
  };

  const keys = Object.keys(keysMap);
  let list = [];

  for (let x = 0; x < keys.length; x++) {
    let label = keysMap[keys[x]];
    let value = keys[x].includes('%') ? payload[keys[x]] + '%' : payload[keys[x]];
    list.push({
      label,
      value,
    });
  }

  let data = {
    live: true,
    value: null,
    label: 'Ranking Insights',
    description: `A live breakdown of your ${groupName.toLowerCase().includes('priority') ? 'priority' : groupName} keywords:`,
    type: 'rankings',
    list,
  };

  action('FORMAT_RANKING_INSIGHTS_CARDS_SUCCESS', {
    data: [{ ...data }],
  });
};

const formatLeadsPerMonth = (payload) => {
  if (payload[0] && payload[1]) {
    if (payload[0].status === 200 && payload[1].status === 200) {
      const payloadOneYearURL = new URLSearchParams(payload[0].config.url)
      const payloadOneURLEndDate = payloadOneYearURL.get('endDate')
      const payloadTwoYearURL = new URLSearchParams(payload[1].config.url)
      const payloadTwoURLEndDate = payloadTwoYearURL.get('endDate')

      const year_payload_one = format(new Date(payloadOneURLEndDate), 'yyyy');
      const year_payload_two = format(new Date(payloadTwoURLEndDate), 'yyyy');
      const payload_one_is_current_year = year_payload_one > year_payload_two ? year_payload_one : year_payload_two;

      const payload_one_with_years_added = payload[0].data.history.map((item) => {
        const date = new Date(`${item.name}/${year_payload_one}`);
        item.date = format(date, 'MM/dd/yyyy');
        item[payload_one_is_current_year ? 'Current year' : 'Previous year'] = item.value;
        item.date_for_sort = date;
        return item;
      });

      const payload_two_with_years_added = payload[1].data.history.map((item) => {
        const date = new Date(`${item.name}/${year_payload_two}`);
        item.date = format(date, 'MM/dd/yyyy');
        item[payload_one_is_current_year ? 'Previous year' : 'Current year'] = item.value;
        item.date_for_sort = date;
        return item;
      });

      payload_two_with_years_added.forEach((item, i) => {
        payload_one_with_years_added[i][payload_one_is_current_year ? 'Previous year' : 'Current year'] = item.value;
      });

      action('FETCHING_LEADS_ANALYTICS_SUCCESS', {
        ...(payload[0].data || {}),
      });

      return { data: payload_one_with_years_added, typesByMonth: payload[0].data.typesByMonth };
    }
  }
  return null;
};

const formatSiteData = (payload) => {
  let target_cost_per_conversion = (payload || {}).target_cost_per_conversion || null;
  if (target_cost_per_conversion === null) {
    target_cost_per_conversion = ((payload || {}).category || {}).target_cost_per_conversion || null;
    if (target_cost_per_conversion === null) {
      target_cost_per_conversion = (((payload || {}).client || {}).category || {}).target_cost_per_conversion || null;
    }
  }
  return {
    ...payload,
    target_cost_per_conversion,
  };
};

const setAdwordsInsightsCards = (payload) => {
  let adwords_insights_cards = [];
  let state = store.getState() || {};
  let publicReportState = state.publicReporting || {};
  let siteData = (publicReportState || {}).site_data;

  if (siteData.target_cost_per_conversion !== null && payload.cost_conversion.length > 0) {
    let currentMonthData = payload.cost_conversion[payload.cost_conversion.length - 1].value;
    let card = {};
    if (currentMonthData !== 0) {
      card.live = false;
      card.label = 'Ads Insights';
      card.description = "Looks like we've hit our target cost per conversion! 🎉 🎉 🎉";
    }

    if (currentMonthData > siteData.target_cost_per_conversion) {
      card.description = `Our digital ads team is constantly updating your ads for best performance! We are $${(currentMonthData - siteData.target_cost_per_conversion).toFixed(2)} away from our target Cost Per Conversion. `;
    }
    if (Object.keys(card).length > 0) {
      adwords_insights_cards.push(card);
    }
  }

  if (payload.conversions.length > 0) {
    let bestMonth = { value: 0 };
    let list = [];
    for (let i = 0; i < payload.conversions.length; i++) {
      let item = payload.conversions[i];
      list.push({
        label: item.name,
        value: item.value,
      });
      if (item.value > bestMonth.value) {
        bestMonth = item;
      }
    }
    const bestMonthDescription = `Your best month was ${bestMonth.name} with ${bestMonth.value} conversions! Here's a full breakdown of your conversions by month:`;

    let card = {
      label: 'Conversions By Month',
      description: (bestMonthDescription || '').includes('undefined') ? '' : bestMonthDescription,
      list,
    };
    adwords_insights_cards.push(card);
  }

  let updatedPayload = {
    ...payload,
    adwords_insights_cards,
  };

  return updatedPayload;
};

export const publicReportingMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    if (action.type === 'FETCHING_SITE_SUCCESS') {
      action.payload = formatSiteData(action.payload);
    }
    if (action.type === 'FETCHING_NEW_VISITORS_SUCCESS') {
      action.payload = formatNewVisitors(action.payload);
    }
    if (action.type === 'FETCHING_RANKING_INSIGHTS_SUCCESS') {
      action.payload = formatRankingInsights(action.payload);
    }
    if (action.type === 'FETCHING_ANALYTIC_INSIGHTS_NEW_VISITORS_SUCCESS') {
      action.payload = formatNewVisitorsAnalytics(action.payload);
    }
    if (action.type === 'FETCHING_RANKING_AVERAGES_SUCCESS') {
      action.payload = formatAverageRankings(action.payload);
    }
    if (action.type === 'FETCHING_LEADS_PER_MONTH_SUCCESS') {
      action.payload = formatLeadsPerMonth(action.payload);
    }
    if (action.type === 'FETCHING_LEADS_ANALYTICS_SUCCESS') {
      action.payload = formatLeadsByMedium(action.payload);
    }
    if (action.type === 'FETCHING_ADWORDS_MTM_SUCCESS') {
      action.payload = setAdwordsInsightsCards(action.payload);
    }
    next(action);
  };
